import { track } from "@/lib/tracking";
import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Brand } from "@/components/Brand";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

export function Shell({ children }) {
  return (
    <div className="min-h-screen grid-bg flex flex-col items-center justify-center p-6">
      <div className="mb-10"><Brand /></div>
      <div className="bg-white border border-slate-200 rounded-lg p-10 max-w-md w-full text-center fade-up">{children}</div>
    </div>
  );
}

export function PaymentSuccess() {
  const [params] = useSearchParams();
  const { refreshUser } = useAuth();
  const [state, setState] = useState("checking");
  const tries = useRef(0);
  useEffect(() => {
    const sid = params.get("session_id");
    let timer;
    if (!sid) { setState("nosession"); return undefined; }
    const check = async () => {
      try {
        const { data } = await api.get(`/payments/status/${sid}`);
        if (data.payment_status === "paid") {
          if (!sessionStorage.getItem(`gv_px_${sid}`)) { sessionStorage.setItem(`gv_px_${sid}`, "1"); track("Purchase", { currency: data.currency || "USD", value: data.amount || 0 }); }
          await refreshUser();
          return setState("paid");
        }
      } catch { /* retry */ }
      if (++tries.current >= 10) return setState("timeout");
      timer = setTimeout(check, 2000);
    };
    check();
    return () => clearTimeout(timer);
  }, [params, refreshUser]);
  return (
    <Shell>
      {state === "checking" && <><Loader2 className="h-10 w-10 animate-spin text-emerald-600 mx-auto" /><h1 className="font-display text-2xl font-extrabold mt-6">Confirming your payment…</h1></>}
      {state === "paid" && <>
        <CheckCircle2 className="h-12 w-12 text-emerald-600 mx-auto" />
        <h1 className="font-display text-3xl font-extrabold mt-6" data-testid="payment-success-title">Payment confirmed</h1>
        <p className="text-slate-600 mt-2">Your purchase is active. Thank you for choosing GrantVerify.</p>
        <div className="mt-8 flex gap-3 justify-center">
          <Link to="/dashboard" data-testid="payment-success-dashboard-link"><Button className="bg-emerald-600 hover:bg-emerald-700">Go to dashboard</Button></Link>
          <Link to="/dashboard/reviews" data-testid="payment-success-reviews-link"><Button variant="outline">My reviews</Button></Link>
        </div>
      </>}
      {state === "nosession" && <>
        <h1 className="font-display text-2xl font-extrabold" data-testid="payment-nosession-title">No payment session found</h1>
        <p className="text-slate-600 mt-2">This link is missing its payment details. You can check your plan and receipts in Billing.</p>
        <Link to="/dashboard/billing" data-testid="payment-nosession-billing-link"><Button className="mt-6 bg-emerald-600 hover:bg-emerald-700">Go to Billing</Button></Link>
      </>}
      {state === "timeout" && <>
        <h1 className="font-display text-2xl font-extrabold">Still processing</h1>
        <p className="text-slate-600 mt-2">We haven't received confirmation yet. Refresh this page in a minute.</p>
        <Link to="/dashboard"><Button className="mt-6" variant="outline">Back to dashboard</Button></Link>
      </>}
    </Shell>
  );
}

export function PaymentCancel() {
  return (
    <Shell>
      <XCircle className="h-12 w-12 text-slate-400 mx-auto" />
      <h1 className="font-display text-3xl font-extrabold mt-6" data-testid="payment-cancel-title">Checkout cancelled</h1>
      <p className="text-slate-600 mt-2">No charge was made.</p>
      <Link to="/dashboard/billing" data-testid="payment-cancel-back-link"><Button className="mt-8 bg-slate-900 hover:bg-slate-800">Back to plans</Button></Link>
    </Shell>
  );
}
